import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import WorldLabel from '../WorldLabel';
import { useWorldStore } from '../../state/worldStore';
import { useCityStore } from '../../city/cityStore';
import { stateAt } from '../../city/cityMotion';
import { serverNow, cityListen } from '../../services/worldSocket';
import { audioEngine } from '../../services/audioEngine';
import { useCutscene, cutsceneBridge } from '../../city/cutscene';
import { roadMapBridge } from '../../render/roadMapBridge';
import { takeAudio, releaseAudio, keepSounding, onAudio, fetchAhead, dropAhead } from '../../services/audioPool';
import { cityAudio } from '../../city/stageBridge';
import { voiceHold, musicHushed, volumeWorks } from '../../city/hearing';

// CityTalk — hearing the artists talk, out in the city.
//
// Khari, 2026-10-23: *"We have 2 ways to listen. One is to press and interact,
// and the other is a proximity system… Conversations overheard should use the
// proximity system, and we could possibly include distant intro statements that
// lure us toward the conversation, loading the rest as we listen."*
//
//   · Each line arrives with the moment it is said (server clock), so everyone
//     standing there hears the same line at the same moment, and somebody who
//     walks up late joins it mid-sentence.
//   · Loudness follows distance. The OPENING lines carry furthest — faint from
//     across the street, enough to turn your head — and the rest is for those
//     who come close.
//   · Being close is what keeps it going: this World tells the server it is
//     listening, and the next line is only written while someone is.
//   · When a visitor talks to an artist, the people standing near see what the
//     ARTIST says (never the visitor's words): Khari, "bystander hears only
//     responses. proximity should be used here."
//
// Bubbles and sound only; the bodies' head and face motion read `speaking`.

// STREET SCALE (a person is ~0.75 units, so a unit is ~2.3 m), matched to the
// salon's television, which Khari found right (rooms are in metres: 100% within
// 2.4 m, nothing past its 8 m ring). Out here these were first ~50 m, then
// ~16 m — still "on the other side of a building a few blocks away"
// (2026-10-23). Now: full beside them, gone ~8 m out; an opening line carries
// a little further, to turn a head; a building in between muffles it.
const LISTEN_ON = 4;         // closer than this, you are listening (it keeps going)
const LISTEN_OFF = 5.5;
const BUBBLE_RANGE = 6;      // ~14 m: you can see them talking a little before you hear them
const LURE_RANGE = 4.5;      // the opening lines are just audible this far out (~10 m)
const HEAR_RANGE = 3.5;      // the rest of the conversation (~8 m, the TV's ring)
const NEAR = 1.0;            // full voice within ~2.4 m (the TV's)
const WALLED = 0.2;          // a building between you and them
const SAID_RANGE = 5;        // an artist's reply to a visitor
const SAID_MS = 9000;
// ONE CONVERSATION AT A TIME, for each listener: the one you stepped into, or
// the nearest. Any other within earshot is a murmur with no bubbles — two at
// one shop front were two voices over each other (2026-10-23).
const MURMUR = 0.12;
// THE SCENE YOU STEPPED INTO plays at one level while it has the camera — the
// shots cut close and wide, and the sound must not follow them (Khari,
// 2026-10-23). Everything else is silent until you have the view back.
const SCENE_LEVEL = 0.9;
// FETCHED BEFORE IT IS SAID (Khari: "a moment of silence watching the
// characters move while the audio is loading… preload… the ones that may be
// within view or proximity"): the next lines of a conversation near you — or
// the one you stepped into — are downloaded ahead and start on their beat.
const PRELOAD_RANGE = 14;    // ~30 m, the cue's range
const PRELOAD_MS = 25000;

/** Is there a building between here and there? (The road map's covered texels.) */
export function walled(ax, az, bx, bz) {
  const m = roadMapBridge.map;
  if (!m?.cls) return false;
  const u = m.unitsPerTexel || m.rect.w / m.w;
  const L = Math.hypot(bx - ax, bz - az);
  for (let s = 0.3; s < L - 0.6; s += 0.3) {
    const i = Math.floor((ax + ((bx - ax) * s) / L - m.rect.minX) / u);
    const r = Math.floor((az + ((bz - az) * s) / L - m.rect.minZ) / u);
    if (i < 0 || r < 0 || i >= m.w || r >= m.h) continue;
    if (m.cls[r * m.w + i] === 3) return true;
  }
  return false;
}

/**
 * Set how loud a clip is — and MUTE it when it should not be heard: iOS ignores
 * `volume` on media elements (always 1), so on a phone the fade is all or
 * nothing, and without this every line played at full from anywhere.
 */
function setLevel(el, v) {
  const x = Math.max(0, Math.min(1, v));
  el.volume = x;
  const mute = x < 0.03;
  if (el.muted !== mute) el.muted = mute;
}

export function volumeAt(d, opening) {
  if (d < NEAR) return 1;
  const far = opening ? LURE_RANGE : HEAR_RANGE;
  if (d >= far) return 0;
  const k = (d - NEAR) / (far - NEAR);
  return Math.pow(1 - k, 1.5) * 0.9;  // falls away a little faster than linear, the way a voice does outdoors
}

// ⚠ AN EXPLICIT WIDTH: a wrapping bubble in a WorldLabel shrink-wraps to its
// longest word without one (a one-letter column — seen 2026-10-23, and on
// 2026-08-04 in the salon). Fixed-size, not distance-scaled, for the same reason
// the nameplates are.
const bubbleStyle = {
  width: 220, maxWidth: 'none', boxSizing: 'border-box', background: 'rgba(16,10,6,0.9)', border: '1px solid rgba(255,178,94,0.45)',
  color: '#ffe9c4', font: '500 12.5px/1.35 system-ui, sans-serif', padding: '7px 11px', borderRadius: 13,
  textAlign: 'left', boxShadow: '0 6px 22px rgba(0,0,0,0.45)', wordBreak: 'break-word', pointerEvents: 'none',
};

// When a line is over. Its scheduled end — and, while its clip is still really
// playing, up to EXTEND_MS past that, but never into the next line. The server
// times lines to their measured clips now; this is the backstop, because a clip
// cut off at a guessed end lost its last few words (2026-10-23).
const EXTEND_MS = 6000;
function lineEnd(l, nextAt, a) {
  const end = l.playAt + (l.dur || 3) * 1000;
  if (a && a.el && !a.el.ended && !a.el.paused && a.el.currentTime > 0) return Math.min(end + EXTEND_MS, nextAt || Infinity);
  return end;
}

export default function CityTalk() {
  const camera = useThree((s) => s.camera);
  const npcList = useWorldStore((s) => s.npcList);
  const heightOf = useMemo(() => {
    const m = new Map();
    for (const n of npcList || []) if (n?.avatarId != null) m.set(Number(n.avatarId), n.height || 0.75);
    return m;
  }, [npcList]);

  const audios = useRef(new Map());     // `${conv}:${idx}` → { el, playAt, end, conv }
  const scores = useRef(new Map());     // story id → { el } — a City Story's song
  const listening = useRef(new Set());  // conv ids this World said it is listening to
  const [bubbles, setBubbles] = useState([]);
  const acc = useRef(0);
  const dipped = useRef(false);
  const sceneHeld = useRef(false);   // iOS: the radio paused for the scene you stepped into
  // ⚠ UNMOUNTING IS LEAVING (2026-10-29): an indoor story scene switches the view
  // into a room, which unmounts the street. Nothing of it keeps playing in there
  // (RoomStoryAudio plays the scene), nothing stays "listening", no dip is left on.
  useEffect(() => () => {
    for (const a of audios.current.values()) { try { a.el?.pause(); } catch { /* gone */ } dropAhead(a.url); }
    for (const s of scores.current.values()) { try { s.el?.pause(); } catch { /* gone */ } }
    audios.current.clear(); scores.current.clear();
    for (const k of listening.current) cityListen(k, false);
    listening.current.clear();
    if (dipped.current) { dipped.current = false; try { audioEngine.dip(false); } catch { /* no engine */ } }
    voiceHold('street', false);
    if (sceneHeld.current) { sceneHeld.current = false; audioEngine.roomQuiet(false, 600, 'scene').catch?.(() => {}); }
  }, []);

  useFrame((_, dt) => {
    acc.current += dt;
    if (acc.current < 0.2) return;   // five times a second is plenty for speech
    acc.current = 0;
    const t = serverNow();
    const cs = useCityStore.getState();
    if (!cs.live) {
      voiceHold('street', false, { releaseMs: 4000 });
      if (sceneHeld.current) { sceneHeld.current = false; audioEngine.roomQuiet(false, 600, 'scene').catch?.(() => {}); }
      return;
    }
    // Your ears are where YOU stand: during a cutscene that is your saved view,
    // not the shot (city/cutscene `at`).
    const cam = (cutsceneBridge.active && cutsceneBridge.at) || camera.position;
    const inCut = useCutscene.getState().phase === 'cut';
    const sc0 = inCut ? cutsceneBridge.scene : null;
    const sceneKey = sc0 ? (sc0.kind === 'conv' ? sc0.id : `${sc0.kind}:${sc0.id}`) : null;   // a set ('stage:…') puts every chat aside
    const where = (id) => {
      const r = cs.byId[id];
      const st = r ? stateAt(r, t, cs.convs) : null;
      return st ? { x: st.x, z: st.z, y: st.y || 0 } : null;
    };
    const next = [];
    const live = new Set();
    const keep = new Set();   // fetched ahead, not due yet
    let capped = null;        // listening to one that will not go on for you (free visit, one used)
    const al = cs.allowance;
    let loudest = 0;
    /* A line of the conversation you ENTERED (stepped into, or following) is
       being heard (voices first: city/hearing). Khari, 2026-11-05: "If a
       conversation is entered on safari … the conversation should win", and of
       one he only walked up to: "the radio playing in the background while the
       initial statement played was correct." */
    let voiced = false;
    // One element per line: made early when it can be (muted, loading), started
    // on its beat; joining late seeks into it.
    const ensure = (key, url, conv, end) => {
      let a = audios.current.get(key);
      if (!a) {
        // Borrowed: on iOS only an element blessed on a tap may start from the
        // frame loop (services/audioPool).
        const el = takeAudio(url);
        el.muted = true;
        try { el.load(); } catch { /* it loads on play */ }
        a = { el, end, conv, started: false, url };
        audios.current.set(key, a);
        // Downloaded whole now, played from memory on its beat (services/audioPool
        // fetchAhead: a phone fetching it only then was late for every line after
        // the first). Swapped in only if it has not started; a copy the player
        // will not take puts the file's own URL back.
        const mine = a;
        fetchAhead(url).then((blob) => {
          if (!blob || audios.current.get(key) !== mine || mine.started) return;
          mine.el.src = blob;
          try { mine.el.load(); } catch { /* loads on play */ }
          onAudio(mine.el, 'error', () => { if (mine.el.src === blob) { mine.el.src = url; try { mine.el.load(); } catch { /* loads on play */ } } }, { once: true });
        });
      }
      return a;
    };
    // Started on its beat (joining late seeks into it), and a refused start asked
    // again while it should be heard (services/audioPool keepSounding: on an
    // iPhone the one play() it had was refused and every line stayed silent).
    const start = (a, l) => keepSounding(a, { at: () => (serverNow() - l.playAt) / 1000, label: 'talk' });

    // Which one this listener is with: the scene they stepped into, else the
    // nearest that is saying something now.
    const tgt = useCutscene.getState().target;
    let focus = sceneKey || (tgt?.kind === 'conv' ? tgt.id : tgt?.kind === 'story' ? `story:${tgt.id}` : null);
    if (!focus) {
      let best = Infinity;
      const talkingNow = (lines) => (lines || []).some((l) => l.playAt && t >= l.playAt && t < l.playAt + (l.dur || 3) * 1000 + 400);
      for (const c of Object.values(cs.convs)) {
        if (!talkingNow(c.lines)) continue;
        const pa = where(c.a); const pb = where(c.b);
        if (!pa || !pb) continue;
        const d = Math.hypot(cam.x - (pa.x + pb.x) / 2, cam.z - (pa.z + pb.z) / 2);
        if (d < best) { best = d; focus = c.id; }
      }
      for (const st of Object.values(cs.stories || {})) {
        if (!talkingNow(st.lines)) continue;
        const pl = where(st.a);
        const d = pl ? Math.hypot(cam.x - pl.x, cam.z - pl.z) : Infinity;
        if (d < best) { best = d; focus = `story:${st.id}`; }
      }
    }

    for (const c of Object.values(cs.convs)) {
      if (!c.lines?.length && c.status !== 'live') continue;
      const pa = where(c.a); const pb = where(c.b);
      if (!pa || !pb) continue;
      const mx = (pa.x + pb.x) / 2; const mz = (pa.z + pb.z) / 2;
      const d = Math.hypot(cam.x - mx, cam.z - mz);
      const aside = !!focus && focus !== c.id;
      // Stepped into it (its cutscene, or following it): you are listening, from anywhere.
      const mine = sceneKey === c.id || (tgt?.kind === 'conv' && tgt.id === c.id);

      // Listening keeps the conversation going (with a little hysteresis).
      const was = listening.current.has(c.id);
      const now = c.status === 'live' && (mine || (was ? d < LISTEN_OFF : d < LISTEN_ON));
      if (now !== was) {
        if (now) listening.current.add(c.id); else listening.current.delete(c.id);
        cityListen(c.id, now);
      }
      if (now && al && !al.paying && al.free && al.free !== c.id && !c.ended) capped = c.id;

      const said = (c.lines || []).filter((l) => l.playAt).sort((x, y) => x.playAt - y.playAt);
      for (let i = 0; i < said.length; i++) {
        const l = said[i];
        const key = `${c.id}:${l.idx}`;
        const end = lineEnd(l, said[i + 1]?.playAt, audios.current.get(key));
        if (t < l.playAt) {
          if (l.audio && l.playAt - t < PRELOAD_MS && (mine || d < PRELOAD_RANGE * (c.origin === 'freestyle' ? 2 : 1))) { ensure(key, l.audio, c.id, end); keep.add(key); }
          continue;
        }
        if (t > end + 400) continue;
        // This line is being said right now.
        cs.setSpeaking(l.speaker, end);
        live.add(key);
        const opening = l.idx <= 1;
        const spk = l.speaker === c.a ? pa : pb;
        const ds = Math.hypot(cam.x - spk.x, cam.z - spk.z);
        // A freestyle is a performance: its verses carry twice as far as a chat.
        const reach = c.origin === 'freestyle' ? 2 : 1;
        const vol = inCut ? (sceneKey === c.id ? SCENE_LEVEL : 0)
          : volumeAt(ds / reach, opening) * (aside ? MURMUR : 1) * (ds < LURE_RANGE * reach && walled(cam.x, cam.z, spk.x, spk.z) ? WALLED : 1);
        let a = audios.current.get(key);
        // Walking away mid-line turns it down too (it used to keep the level it
        // started at, so a long line followed you down the street).
        if (a && vol <= 0.01) setLevel(a.el, 0);
        if (vol > 0.01 && l.audio) {
          a = ensure(key, l.audio, c.id, end);
          start(a, l);
          a.end = end;
          setLevel(a.el, vol);
          loudest = Math.max(loudest, vol);
          // Only the one you stepped into or are following wins over the music; walked past, the radio plays on under it (Khari).
          if (vol >= 0.03 && mine) voiced = true;
        }
        // In a cutscene the line is the caption on the letterbox, not a bubble.
        if (d < BUBBLE_RANGE && !inCut && !aside) {
          const p = l.speaker === c.a ? pa : pb;
          next.push({ key, text: l.text, pos: [p.x, (p.y || 0) + (heightOf.get(l.speaker) || 0.75) + 0.32, p.z], far: d > HEAR_RANGE });
        }
      }
    }

    // ── City Stories: each artist's episode (lines by their speaker, and its song) ──
    const liveScores = new Set();
    for (const st of Object.values(cs.stories || {})) {
      if (t < st.t0 - 8 * 60000 || t > st.t1 + 10000) continue;
      const lead = where(st.a);
      if (!lead) continue;
      const dLead = Math.hypot(cam.x - lead.x, cam.z - lead.z);
      // Close enough to hear = the next lines are voiced (the server's lazy rule).
      const skey = `story:${st.id}`;
      const aside = !!focus && focus !== skey;
      const mineS = sceneKey === skey || (tgt?.kind === 'story' && tgt.id === st.id);
      const was = listening.current.has(skey);
      const on = t <= st.t1 && (mineS || (was ? dLead < LISTEN_OFF + 6 : dLead < LISTEN_ON + 6));
      if (on !== was) {
        if (on) listening.current.add(skey); else listening.current.delete(skey);
        cityListen(skey, on);
      }
      let lineOn = false;
      const sl = [...(st.lines || [])].sort((x, y) => x.playAt - y.playAt);
      for (let i = 0; i < sl.length; i++) {
        const l = sl[i];
        const key = `${skey}:${l.idx}`;
        const end = lineEnd(l, sl[i + 1]?.playAt, audios.current.get(key));
        if (t < l.playAt) {
          if (l.audio && l.playAt - t < PRELOAD_MS && (mineS || dLead < PRELOAD_RANGE)) { ensure(key, l.audio, skey, end); keep.add(key); }
          continue;
        }
        if (t > end + 400) continue;
        lineOn = true;
        cs.setSpeaking(l.speaker, end);
        const sp = where(l.speaker) || lead;
        const d = Math.hypot(cam.x - sp.x, cam.z - sp.z);
        live.add(key);
        // A scene INSIDE a venue (RoomStory plays it in the room): out here it is
        // heard through the wall, and nobody's bubble floats at the door. Someone
        // watching the episode still hears it (the scene plays at one level).
        const indoors = !!st.scenes?.[l.scene]?.r;
        const vol = inCut ? (sceneKey === skey ? SCENE_LEVEL : 0)
          : volumeAt(d, l.idx <= 1) * (aside ? MURMUR : 1) * ((indoors || (d < LURE_RANGE && walled(cam.x, cam.z, sp.x, sp.z))) ? WALLED : 1);
        let a = audios.current.get(key);
        if (a && vol <= 0.01) setLevel(a.el, 0);
        if (vol > 0.01 && l.audio) {
          a = ensure(key, l.audio, skey, end);
          start(a, l);
          a.end = end;
          setLevel(a.el, vol);
          loudest = Math.max(loudest, vol);
          if (vol >= 0.03 && mineS) voiced = true;
        }
        if (d < BUBBLE_RANGE && !inCut && !aside && !indoors) {
          next.push({ key, text: l.text, pos: [sp.x, (sp.y || 0) + (heightOf.get(l.speaker) || 0.75) + 0.32, sp.z], far: d > HEAR_RANGE });
        }
      }
      // THE SONG that scores it, from the lead, on the episode's clock: a late
      // arrival hears the right bar. It steps back under the lines and fades out
      // at the end (a long song is cut at the episode's length).
      if (st.score?.url && t >= st.t0 && t <= st.t1) {
        // Music carries a little further than a voice (~14 m), never across the district.
        const base = inCut ? (sceneKey === skey ? 0.45 : 0)
          : (dLead < 1.5 ? 0.5 : dLead < 6 ? 0.5 * (1 - (dLead - 1.5) / 4.5) : 0) * (aside ? 0.4 : 1)
            * (dLead < 6 && walled(cam.x, cam.z, lead.x, lead.z) ? WALLED * 1.5 : 1);
        const fade = Math.min(1, (st.t1 - t) / 3000, (t - st.t0) / 1500 + 0.2);
        const vol = base * (lineOn ? 0.35 : 1) * Math.max(0, fade);
        let sc = scores.current.get(st.id);
        if (vol > 0.01) {
          if (!sc) {
            const el = takeAudio(st.score.url);
            sc = { el, started: false };
            scores.current.set(st.id, sc);
          }
          const want = (t - st.t0) / 1000;
          const wasStarted = sc.started;
          keepSounding(sc, { at: () => (serverNow() - st.t0) / 1000, label: 'score' });
          if (wasStarted && !sc.el.paused && sc.el.readyState >= 2 && Math.abs(sc.el.currentTime - want) > 0.8) {
            try { sc.el.currentTime = want; } catch { /* ignore */ }
          }
          // Under the lines on an iPhone, where it cannot be turned down: muted while they are heard (city/hearing).
          setLevel(sc.el, musicHushed() ? 0 : vol);
          loudest = Math.max(loudest, vol * 1.2);
          liveScores.add(st.id);
        }
      }
    }
    for (const [id, sc] of scores.current) {
      if (liveScores.has(id)) continue;
      releaseAudio(sc.el);
      scores.current.delete(id);
    }

    // An artist's reply to a visitor, for whoever is standing near them.
    for (const [id, s] of Object.entries(cs.said || {})) {
      if (t - s.at > SAID_MS && Date.now() - s.at > SAID_MS) continue;
      const p = where(Number(id));
      if (!p) continue;
      if (Math.hypot(cam.x - p.x, cam.z - p.z) > SAID_RANGE) continue;
      next.push({ key: `said:${id}:${s.at}`, text: s.text, pos: [p.x, (p.y || 0) + (heightOf.get(Number(id)) || 0.75) + 0.32, p.z], far: false });
    }

    if ((cs.capped || null) !== capped) useCityStore.setState({ capped });

    // Lines that are over, or out of earshot: let them go.
    for (const [key, a] of audios.current) {
      if ((!live.has(key) && !keep.has(key)) || (a.started && t > a.end + 1500)) {
        releaseAudio(a.el);
        dropAhead(a.url);
        audios.current.delete(key);
      }
    }
    // The street's music steps back a little while a conversation is close.
    const dip = loudest > 0.3;
    if (dip !== dipped.current) { dipped.current = dip; try { audioEngine.dip(dip); } catch { /* no engine */ } }
    cityAudio.speech = dip;   // the street's music sources step back too (CitySounds)
    // Where volume does not work (iOS), the music steps OUT while a line is heard, and back a beat after the last.
    voiceHold('street', voiced, { releaseMs: 4000 });
    // …and a scene you STEPPED INTO has the sound to itself there: the radio is
    // paused for it (the room-hold a venue uses), not just muted, and comes back after.
    const sceneOn = !volumeWorks && inCut && !!sceneKey;
    if (sceneOn !== sceneHeld.current) { sceneHeld.current = sceneOn; audioEngine.roomQuiet(sceneOn, 300, 'scene').catch?.(() => {}); }

    // Bubbles only re-render when what is said changes.
    const sig = next.map((b) => b.key).join('|');
    if (sig !== (bubbles.sig || '')) { const arr = next; arr.sig = sig; setBubbles(arr); }
  });

  return (
    <group>
      {bubbles.map((b) => (
        <WorldLabel key={b.key} position={b.pos} center occlude={false} zIndexRange={[18, 0]} style={{ pointerEvents: 'none' }}>
          {/* Anchored on the nameplate's own point (their feet + height + 0.32,
              CityResident) and lifted clear of it: the bubble's bottom sits above
              the nameplate's top. It used to be anchored without the ground they
              stand on, a little lower, and up close the name covered its last
              lines (2026-10-23). */}
          <div style={{ ...bubbleStyle, opacity: b.far ? 0.72 : 1, transform: 'translateY(calc(-50% - 24px))' }}>{b.text}</div>
        </WorldLabel>
      ))}
    </group>
  );
}
